import { useCallback, useEffect, useRef, useState } from "react"

import {
  hashQueryKey,
  type QueryKey,
  useQueryClient,
} from "@tanstack/react-query"

import {
  type UpdateFix,
  updatesPlan,
  type UpdatesSpec,
} from "./proposal-updates"

/** How long fixed rows stay green before the panel leaves. */
export const UPDATES_SETTLE_MS = 1500

/** Why a step stopped a run, in its own copy, and where it's fixed instead. */
export type UpdateStop = {
  readonly message: string
  readonly fix: UpdateFix | null
}

export type UpdatesRunner<K extends string> = {
  /** The update running now. */
  readonly running: K | null
  /** What the run in progress was asked for. */
  readonly target: K | `all` | null
  /** Updates finished since the panel last cleared; their rows show green. */
  readonly done: ReadonlySet<K>
  /** The step that stopped the last run. */
  readonly failure: (UpdateStop & { readonly key: K }) | null
  /**
   * Runs `target` after whatever it depends on, or every update in fix order.
   * Each step decides from the reads as the previous step left them, so a
   * later step skips what an earlier one made current. A failing step stops
   * the run.
   */
  readonly run: (target: K | `all`) => void
}

/**
 * One Proposal kind's updates, as the runner needs them. Callers memoize it:
 * a new config makes a new `run`.
 */
export type UpdatesRunnerConfig<K extends string, Reads> = {
  readonly spec: Pick<UpdatesSpec<K>, `dependencies` | `order`>
  /** The read that says whether each update is needed; refetched after every step. */
  readonly readKeys: Readonly<Record<K, QueryKey>>
  /** The reads as the cache holds them now. */
  readonly readsNow: () => Reads
  readonly isNeeded: (key: K, reads: Reads) => boolean
  /**
   * Why the panel can't run a needed update itself. What builds on it waits
   * too, and the failure links to its fix.
   */
  readonly unrunnable: (key: K) => UpdateStop | null
  /** Each update's existing command. */
  readonly steps: Readonly<Record<K, () => Promise<unknown>>>
  /** A failed command, in the copy its inline action uses. */
  readonly describeFailure: (key: K, error: unknown) => UpdateStop
}

/**
 * The Updates panel's runner, on a Proposal kind's existing commands. Each
 * command keeps its own cache invalidation; the runner only orders them and
 * reads the result back before deciding the next. Nothing runs until the
 * seller asks.
 */
export const useUpdatesRunner = <K extends string, Reads>({
  config,
  settled,
}: {
  config: UpdatesRunnerConfig<K, Reads>
  /** Nothing is out of date any more: fixed rows clear after a moment. */
  settled: boolean
}): UpdatesRunner<K> => {
  const queryClient = useQueryClient()
  const [running, setRunning] = useState<K | null>(null)
  const [target, setTarget] = useState<K | `all` | null>(null)
  const [done, setDone] = useState<ReadonlySet<K>>(new Set())
  const [failure, setFailure] = useState<UpdatesRunner<K>[`failure`]>(null)
  // A second click while a run is in flight is ignored, not queued.
  const inFlight = useRef(false)

  const run = useCallback(
    (requested: K | `all`): void => {
      if (inFlight.current) return
      inFlight.current = true
      setFailure(null)
      setTarget(requested)

      const { spec, readKeys } = config
      // A read never loaded, or whose last check failed, can't say whether
      // its update is needed.
      const known = (key: K): boolean => {
        const state = queryClient.getQueryState(readKeys[key])
        return state?.data !== undefined && state.status !== `error`
      }

      void (async () => {
        try {
          for (const key of updatesPlan({ spec, target: requested })) {
            // Everything after an unknown read would build on a guess.
            if (!known(key)) return
            if (config.isNeeded(key, config.readsNow())) {
              const stop = config.unrunnable(key)
              if (stop) {
                setFailure({ key, ...stop })
                return
              }
              setRunning(key)
              try {
                await config.steps[key]()
              } catch (error) {
                setFailure({ key, ...config.describeFailure(key, error) })
                return
              }
              setDone((current) => new Set(current).add(key))
              // What the step left out of date decides the next one. Updates
              // can share a read; each is fetched once.
              const reads = new Map(
                spec.order.map((read) => [
                  hashQueryKey(readKeys[read]),
                  readKeys[read],
                ]),
              )
              await Promise.all(
                [...reads.values()].map((queryKey) =>
                  queryClient.refetchQueries({ queryKey, exact: true }),
                ),
              )
            }
          }
        } finally {
          inFlight.current = false
          setRunning(null)
          setTarget(null)
        }
      })()
    },
    [config, queryClient],
  )

  // A failure the reads have overtaken (the update committed after all) is
  // forgotten once nothing is out of date, so the next cycle starts clean.
  if (settled && running === null && failure !== null) setFailure(null)

  // Fixed rows stay green for a moment, then the panel slides away.
  const hasDone = done.size > 0
  useEffect(() => {
    if (!settled || running !== null || !hasDone) return
    const timer = setTimeout(() => setDone(new Set()), UPDATES_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [hasDone, running, settled])

  return { running, target, done, failure, run }
}
